import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '@/views/login/index.vue'
import Layout from '@/layout/index.vue'
import Dashboard from '@/views/dashboard/index.vue'
import Admin from '@/views/admin/index.vue'
import Role from '@/views/role/index.vue'
import User from '@/views/user/index.vue'
import Verify from '@/views/verify/index.vue'
import Note from '@/views/note/index.vue'
import School from '@/views/school/index.vue'
import Moment from '@/views/moment/index.vue'
import ContentAudit from '@/views/audit/index.vue'
import Report from '@/views/report/index.vue'
import RechargeOrder from '@/views/recharge/order.vue'
import RechargePackage from '@/views/recharge/package.vue'
import RechargeBalance from '@/views/recharge/balance.vue'
import RechargeCard from '@/views/recharge/card.vue'
import Announcement from '@/views/announcement/index.vue'
import File from '@/views/file/index.vue'
import Config from '@/views/config/index.vue'
import SensitiveWord from '@/views/sensitive-word/index.vue'
import FriendLink from '@/views/friend-link/index.vue'
import NoticeGroup from '@/views/notice/group.vue'
import AdAnalytics from '@/views/stats/ad.vue'
import RevenueStats from '@/views/stats/revenue.vue'
import SystemMonitor from '@/views/monitor/index.vue'
import Cookies from 'js-cookie'
import store from '@/store'
import { formatPageTitle } from '@/utils/system-info'

Vue.use(VueRouter)

/**
 * 路由表 = 系统全部页面。
 * 谁能看到哪些，由服务端下发的 menus（菜单 key = 完整 path）决定，
 * 侧边栏在 layout 里做过滤，访问控制在本文件的守卫里做。
 *
 * ⚠️ 新增页面时：① 这里加路由 ② 后端 MenuConstant.ALL 加一条 ③ 超管在「角色权限」里勾上。
 */
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: '登录' },
    hidden: true
  },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: { title: '首页', icon: 'el-icon-s-home' }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/admin',
    meta: { title: '系统管理', icon: 'el-icon-setting' },
    children: [
      {
        path: 'admin',
        name: 'Admin',
        component: Admin,
        meta: { title: '管理员管理', icon: 'el-icon-user-solid' }
      },
      {
        path: 'role',
        name: 'RolePermission',
        component: Role,
        meta: { title: '角色权限', icon: 'el-icon-key' }
      },
      {
        path: 'config',
        name: 'Config',
        component: Config,
        meta: { title: '系统配置', icon: 'el-icon-s-tools' }
      },
      {
        path: 'sensitive-word',
        name: 'SensitiveWord',
        component: SensitiveWord,
        meta: { title: '敏感词管理', icon: 'el-icon-warning' }
      },
      {
        path: 'friend-link',
        name: 'FriendLink',
        component: FriendLink,
        meta: { title: '友情链接', icon: 'el-icon-link' }
      },
      {
        // 通知群配置：只给超管。后端接口里还另外校验了一次角色 ——
        // 前端的菜单过滤只是"看不见入口"，请求照样发得出去。
        path: 'notice-group',
        name: 'NoticeGroup',
        component: NoticeGroup,
        meta: { title: '通知群配置', icon: 'el-icon-bell' }
      },
      {
        // 系统监控：只给超管（后端 MenuConstant 标了 superOnly）。
        // 接口指标、IP 规则、审计日志都是平台级信息，不给学校管理员。
        path: 'monitor',
        name: 'SystemMonitor',
        component: SystemMonitor,
        meta: { title: '系统监控', icon: 'el-icon-monitor' }
      }
    ]
  },
  {
    path: '/user',
    component: Layout,
    redirect: '/user/list',
    meta: { title: '用户管理', icon: 'el-icon-user' },
    children: [
      {
        path: 'list',
        name: 'User',
        component: User,
        meta: { title: '用户列表', icon: 'el-icon-document' }
      }
    ]
  },
  {
    path: '/verify',
    component: Layout,
    redirect: '/verify/audit',
    meta: { title: '认证管理', icon: 'el-icon-postcard' },
    children: [
      {
        path: 'audit',
        name: 'VerifyAudit',
        component: Verify,
        meta: { title: '认证审核', icon: 'el-icon-s-check' }
      }
    ]
  },
  {
    path: '/note',
    component: Layout,
    redirect: '/note/list',
    meta: { title: '纸条管理', icon: 'el-icon-message' },
    children: [
      {
        path: 'list',
        name: 'Note',
        component: Note,
        meta: { title: '纸条列表', icon: 'el-icon-tickets' }
      }
    ]
  },
  {
    path: '/school',
    component: Layout,
    redirect: '/school/list',
    meta: { title: '学校管理', icon: 'el-icon-office-building' },
    children: [
      {
        path: 'list',
        name: 'School',
        component: School,
        meta: { title: '学校列表', icon: 'el-icon-school' }
      }
    ]
  },
  {
    path: '/moment',
    component: Layout,
    redirect: '/moment/list',
    meta: { title: '动态管理', icon: 'el-icon-picture' },
    children: [
      {
        path: 'list',
        name: 'Moment',
        component: Moment,
        meta: { title: '动态列表', icon: 'el-icon-picture-outline' }
      }
    ]
  },
  {
    // 内容审核：机器拿不准的动态在这里人工复核。
    // path 必须与后端 MenuConstant 的 key（/audit/moment）一致，
    // 否则侧边栏点进来会被路由守卫判成"无权限"。
    path: '/audit',
    component: Layout,
    redirect: '/audit/moment',
    meta: { title: '动态管理', icon: 'el-icon-picture' },
    children: [
      {
        path: 'moment',
        name: 'ContentAudit',
        component: ContentAudit,
        meta: { title: '内容审核', icon: 'el-icon-view' }
      }
    ]
  },
  {
    path: '/report',
    component: Layout,
    redirect: '/report/list',
    meta: { title: '动态管理', icon: 'el-icon-picture' },
    children: [
      {
        path: 'list',
        name: 'ReportManage',
        component: Report,
        meta: { title: '举报管理', icon: 'el-icon-warning-outline' }
      }
    ]
  },
  {
    path: '/recharge',
    component: Layout,
    redirect: '/recharge/order',
    meta: { title: '充值管理', icon: 'el-icon-wallet' },
    children: [
      {
        path: 'order',
        name: 'RechargeOrder',
        component: RechargeOrder,
        meta: { title: '订单管理', icon: 'el-icon-document-copy' }
      },
      {
        path: 'package',
        name: 'RechargePackage',
        component: RechargePackage,
        meta: { title: '爱豆套餐', icon: 'el-icon-goods' }
      },
      {
        path: 'balance',
        name: 'RechargeBalance',
        component: RechargeBalance,
        meta: { title: '爱豆充值', icon: 'el-icon-money' }
      },
      {
        path: 'card',
        name: 'RechargeCard',
        component: RechargeCard,
        meta: { title: '充值卡管理', icon: 'el-icon-postcard' }
      }
    ]
  },
  {
    path: '/announcement',
    component: Layout,
    redirect: '/announcement/list',
    meta: { title: '公告管理', icon: 'el-icon-bell' },
    children: [
      {
        path: 'list',
        name: 'Announcement',
        component: Announcement,
        meta: { title: '公告列表', icon: 'el-icon-chat-dot-round' }
      }
    ]
  },
  {
    // 数据统计：两个页面的权限模型**不同**，别混。
    //   /stats/ad      仅超管（superOnly）—— 要下钻到每个用户的观看行为
    //   /stats/revenue 超管 + 管理员（minRole=admin）—— 校管该知道本校营收
    // 两者的侧边栏可见性都由 filterRoutesByMenus 按各自 key 判断。
    path: '/stats',
    component: Layout,
    redirect: '/stats/revenue',
    meta: { title: '数据统计', icon: 'el-icon-data-analysis' },
    children: [
      {
        path: 'revenue',
        name: 'RevenueStats',
        component: RevenueStats,
        meta: { title: '收益统计', icon: 'el-icon-money' }
      },
      {
        path: 'ad',
        name: 'AdAnalytics',
        component: AdAnalytics,
        meta: { title: '广告分析', icon: 'el-icon-video-camera' }
      }
    ]
  },
  {
    path: '/file',
    component: Layout,
    redirect: '/file/list',
    meta: { title: '文件管理', icon: 'el-icon-folder-opened' },
    children: [
      {
        path: 'list',
        name: 'File',
        component: File,
        meta: { title: '文件管理', icon: 'el-icon-picture-outline' }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach((to, from, next) => {
  // 系统名取自系统配置（system.name），不再写死
  document.title = formatPageTitle(to.meta.title, store.getters.systemName)

  if (to.path === '/login') {
    next()
    return
  }

  const token = Cookies.get('admin_token')
  if (!token) {
    next('/login')
    return
  }

  // 菜单还没下发时放行，让 layout 先把 profile 拉回来再判断 ——
  // 否则首次进后台会被自己的守卫踢回去。
  const menus = store.getters.menus
  if (!menus || !menus.length) {
    next()
    return
  }

  if (to.path === '/' || menus.includes(to.path)) {
    next()
    return
  }

  // 没有该页面的权限：落到第一个有权限的页面，而不是停在空白页
  const fallback = menus[0]
  if (fallback && fallback !== to.path) {
    next(fallback)
    return
  }
  next(false)
})

export default router
